/* eslint-disable semi */
import React from "react";
import { formatNumber } from "../Utility";
import { getRequiredError, IField } from './FieldValidation';
import { FormItem, FormItemProps } from './FormItem';

export interface NumberFieldCallback { (value: number): any }

export interface NumberFieldProps extends FormItemProps {
    id: string
    disabled?: boolean
    onChange: NumberFieldCallback
    value: number
    maxLength?: number
    min?: number
    minMessage?: string
    max?: number
    maxMessage?: string
    allowZero?: boolean
    allowNegative?: boolean
    wholeNumbersOnly?: boolean
    required?: boolean
    requiredMessage?: string
    block?: boolean
    negativeValueMessage?: string
}

export interface NumberFieldState {
    value: string
    errorMessage?: string
}

export class NumberField extends React.Component<NumberFieldProps, NumberFieldState> implements IField {
  input: HTMLInputElement

  constructor(props?: NumberFieldProps, context?: any) {
    super(props, context);

    this.state = {
      value: this.format(this.props.value)
    };
  }

  format(v: number) {
    return formatNumber(v);
  }

  getWholeNumbersOnly(): boolean {
    return this.props.wholeNumbersOnly === true;
  }

  parse(v: string) {
    v = v.replace(/[^\d\.\-]/g, '');

    if (!v) {
      return null;
    }
    let res = Number(v);
    if (Number.isNaN(res)) res = null;

    if (this.getWholeNumbersOnly()) res = Math.round(res)

    return res;
  }

  componentDidUpdate(prevProps: NumberFieldProps, prevState: NumberFieldState) {
    // Only update the state if the value has changed and there is no error message
    if (this.state.value == prevState.value && this.state.value != this.format(this.props.value) && !this.state.errorMessage) {
      const formattedValue = this.format(this.props.value)
      this.setState({ value: formattedValue }, () => {
        if (this.state.errorMessage) {
          this.validate(() => { });
        }
      })
    } else if (this.props.value != prevProps.value) {
      if (this.state.errorMessage) {
        this.validate(() => { });
      }
    }
  }

  onInput(v: string) {
    this.setState({ value: v });
  }

  onChange(v: string) {
    const parsedValue = this.parse(v)

    if (parsedValue != null) {
      if (this.props.min != null && parsedValue < this.props.min) {
        const errorMessage = this.props.minMessage || `Value cannot be less than ${this.props.min}`
        this.setError(errorMessage)
        return;
      }
      if (this.props.max != null && parsedValue > this.props.max) {
        const errorMessage = this.props.maxMessage || `Value cannot exceed ${this.props.max}`
        this.setError(errorMessage)
        return;
      }
    }
    this.props.onChange(parsedValue)
  }

  render() {
    if (this.props.preview) {
      return <FormItem {...this.props} errorMessage={this.props.errorMessage || this.state.errorMessage}>
        <div className="form-value">{this.state.value ? this.state.value : "--"}</div>
      </FormItem>
    }

    return (
      <FormItem {...this.props} inputId={this.props.id} errorMessage={this.props.errorMessage || this.state.errorMessage}>
        <div>
          <input
            className={`emdg-text-input ` +
                    `${this.props.block === false ? '' : 'emdg-text-input--block'}` +
                    `${(this.props.errorMessage || this.state.errorMessage) ? ' emdg-text-input--invalid' : ''}`}
            ref={r => this.input = r}
            type={this.getFieldType()}
            id={this.props.id}
            value={this.state.value}
            onChange={e => this.onInput(e.target.value)}
            onBlur={e => this.onChange((e.target as HTMLInputElement).value)}
            disabled={this.props.disabled}
            maxLength={this.props.maxLength}
            autoComplete="off"
          />
        </div>
      </FormItem>
    )
  }

  getValue(): number {
    return this.props.value;
  }

  hasValue(): boolean {
    let v = this.getValue();
    return (this.props.value != null);
  }

  getFieldType(): string {
    return "text";
  }

  focus() {
    this.input.focus();
  }

  getErrorText(): string {
    let val = this.getValue();
    if (this.props.required === true) {
      if (val == null) {
        return getRequiredError(this.props);
      }
    }
    if (val != null) {
      if (this.props.min != null && this.props.min != undefined && val < this.props.min)
        return this.props.minMessage || `Value cannot be less than ${this.props.min}`

      if (this.props.max != null && this.props.max != undefined && val > this.props.max)
        return this.props.maxMessage || `Value cannot exceed ${this.props.max}`
    }

    if (!this.props.allowNegative && val < 0) {
      return this.props.negativeValueMessage || `Negative value is not allowed ${val}`
    }

    if (this.props.allowZero === false && val === 0) {
      return `0 is not allowed`
    }

    return null;
  }

  validate(callback: { (res: boolean): any }, focus?: boolean) {
    let errorText = this.getErrorText();

    this.setError(errorText, () => {
      if (this.props.errorMessage || errorText) {
        if (focus === true) {
          this.focus();
        }
      }
      callback && callback((this.props.errorMessage || errorText) ? false : true);
    })
  }

  setError(errorMessage?: string, callback?: { (): any }) {
    this.setState({ errorMessage }, callback);
  }
}
